import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

// Formatea un RUT chileno: XX.XXX.XXX-X
const formatRut = (value) => {
  let clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (!clean) return '';
  // Separar cuerpo y dígito verificador
  let dv = clean.slice(-1);
  let body = clean.slice(0, -1);
  if (!body) return dv;
  // Agregar puntos al cuerpo
  let formatted = '';
  let count = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    formatted = body[i] + formatted;
    count++;
    if (count % 3 === 0 && i > 0) formatted = '.' + formatted;
  }
  return formatted + '-' + dv;
};
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Pencil, Trash2, Building2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

export const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    rut: '',
    direccion: '',
    telefono: '',
    centros_costo: []
  });
  const [nuevoCentroCosto, setNuevoCentroCosto] = useState('');

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingCliente(cliente);
      setFormData({
        nombre: cliente.nombre || '',
        rut: cliente.rut ? formatRut(cliente.rut) : '',
        direccion: cliente.direccion || '',
        telefono: cliente.telefono || '',
        centros_costo: cliente.centros_costo || []
      });
    } else {
      setEditingCliente(null);
      setFormData({
        nombre: '',
        rut: '',
        direccion: '',
        telefono: '',
        centros_costo: []
      });
    }
    setNuevoCentroCosto('');
    setDialogOpen(true);
  };

  const handleAddCentroCosto = () => {
    if (nuevoCentroCosto.trim() && !formData.centros_costo.includes(nuevoCentroCosto.trim())) {
      setFormData({
        ...formData,
        centros_costo: [...formData.centros_costo, nuevoCentroCosto.trim()]
      });
      setNuevoCentroCosto('');
    }
  };

  const handleRemoveCentroCosto = (centro) => {
    setFormData({
      ...formData,
      centros_costo: formData.centros_costo.filter(c => c !== centro)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    // Validar formato de teléfono: +569XXXXXXXX
    if (formData.telefono && !/^\+569\d{8}$/.test(formData.telefono)) {
      toast.error('El teléfono debe tener el formato +569XXXXXXXX');
      return;
    }

    try {
      if (editingCliente) {
        await api.put(`/clientes/${editingCliente.id}`, formData);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clientes', formData);
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    }
  };

  const handleDelete = async (cliente) => {
    if (!window.confirm(`¿Eliminar cliente "${cliente.nombre}"?`)) return;
    
    try {
      await api.delete(`/clientes/${cliente.id}`);
      toast.success('Cliente eliminado');
      loadClientes();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const filteredClientes = clientes.filter(c =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.rut && c.rut.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Clientes</h1>
          <p className="text-gray-600 mt-1">Gestiona las empresas que contratan tus servicios de transporte</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-cliente-button">
          <Plus size={18} className="mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 size={20} />
              Lista de Clientes ({filteredClientes.length})
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-cliente-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : filteredClientes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No se encontraron clientes' : 'No hay clientes registrados'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Dirección</TableHead>
                  <TableHead>Centros de Costo</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClientes.map((cliente) => (
                  <TableRow key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
                    <TableCell className="font-medium">{cliente.nombre}</TableCell>
                    <TableCell>{cliente.rut ? formatRut(cliente.rut) : '-'}</TableCell>
                    <TableCell>{cliente.telefono || '-'}</TableCell>
                    <TableCell>{cliente.direccion || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {cliente.centros_costo && cliente.centros_costo.length > 0 ? (
                          cliente.centros_costo.map((centro, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {centro}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(cliente)}
                        data-testid={`edit-cliente-${cliente.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(cliente)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-cliente-${cliente.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="nombre">Nombre de la Empresa *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Minera Los Andes S.A."
                  data-testid="cliente-nombre-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="rut">RUT</Label>
                  <Input
                    id="rut"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                    placeholder="76.xxx.xxx-x"
                    data-testid="cliente-rut-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+569XXXXXXXX"
                    data-testid="cliente-telefono-input"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Av. Principal 123, Ciudad"
                  data-testid="cliente-direccion-input"
                />
              </div>
              
              {/* Centros de Costo */}
              <div className="grid gap-2">
                <Label>Centros de Costo</Label>
                <div className="flex gap-2">
                  <Input
                    value={nuevoCentroCosto}
                    onChange={(e) => setNuevoCentroCosto(e.target.value)}
                    placeholder="Ej: Operaciones, Gerencia..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCentroCosto())}
                    data-testid="centro-costo-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddCentroCosto}>
                    <Plus size={16} />
                  </Button>
                </div>
                {formData.centros_costo.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.centros_costo.map((centro, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1 pr-1">
                        {centro}
                        <button
                          type="button"
                          onClick={() => handleRemoveCentroCosto(centro)}
                          className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Los centros de costo permiten categorizar los servicios solicitados por este cliente
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-cliente-button">
                {editingCliente ? 'Guardar Cambios' : 'Crear Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
